import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { getQuestions, deleteQuestion, getTopicsList, parseOptions } from '../../services/adminApi';
import { toast } from 'react-toastify';

const QuestionsList = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(null);
  
  // Pagination state
  const [pagination, setPagination] = useState({
    page: parseInt(searchParams.get('page')) || 1,
    pageSize: parseInt(searchParams.get('page_size')) || 20,
    total: 0,
    totalPages: 0
  });
  
  // Filter state
  const [filters, setFilters] = useState({
    search: searchParams.get('search') || '',
    topic: searchParams.get('topic') || '',
    difficulty: searchParams.get('difficulty') || '',
    questionType: searchParams.get('question_type') || '',
    sortBy: searchParams.get('sort_by') || 'id',
    sortOrder: searchParams.get('sort_order') || 'desc'
  });
  
  const [topics, setTopics] = useState([]);
  const [searchInput, setSearchInput] = useState(filters.search);

  // Fetch topics list
  useEffect(() => {
    const fetchTopics = async () => {
      try {
        const data = await getTopicsList();
        setTopics(data.topics || []);
      } catch (error) {
        console.error('Error fetching topics:', error);
      }
    };
    fetchTopics();
  }, []);

  // Fetch questions
  useEffect(() => {
    fetchQuestions();
  }, [pagination.page, filters]);

  const fetchQuestions = async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.page,
        page_size: pagination.pageSize,
        ...(filters.search && { search: filters.search }),
        ...(filters.topic && { topic: filters.topic }),
        ...(filters.difficulty && { difficulty: filters.difficulty }),
        ...(filters.questionType && { question_type: filters.questionType }),
        sort_by: filters.sortBy,
        sort_order: filters.sortOrder
      };
      
      const data = await getQuestions(params);
      setQuestions(data.questions || []);
      setPagination(prev => ({
        ...prev,
        total: data.total || 0,
        totalPages: data.total_pages || 0
      }));
    } catch (error) {
      toast.error(`Failed to fetch questions: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Update URL params when filters change
  useEffect(() => {
    const params = new URLSearchParams();
    params.set('page', pagination.page.toString());
    params.set('page_size', pagination.pageSize.toString());
    
    if (filters.search) params.set('search', filters.search);
    if (filters.topic) params.set('topic', filters.topic);
    if (filters.difficulty) params.set('difficulty', filters.difficulty);
    if (filters.questionType) params.set('question_type', filters.questionType);
    if (filters.sortBy) params.set('sort_by', filters.sortBy);
    if (filters.sortOrder) params.set('sort_order', filters.sortOrder);
    
    setSearchParams(params);
  }, [pagination.page, pagination.pageSize, filters]);

  // Handle search with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput !== filters.search) {
        setFilters(prev => ({ ...prev, search: searchInput }));
        setPagination(prev => ({ ...prev, page: 1 }));
      }
    }, 300);
    
    return () => clearTimeout(timer);
  }, [searchInput]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleClearFilters = () => {
    setSearchInput('');
    setFilters({
      search: '',
      topic: '',
      difficulty: '',
      questionType: '',
      sortBy: 'id',
      sortOrder: 'desc'
    });
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (id, questionText) => {
    if (!window.confirm(`Are you sure you want to delete this question?\n\n"${questionText.substring(0, 100)}..."`)) {
      return;
    }
    
    setDeleting(id);
    try {
      await deleteQuestion(id);
      toast.success('Question deleted successfully!');
      fetchQuestions();
    } catch (error) {
      toast.error(`Failed to delete question: ${error.message}`);
    } finally {
      setDeleting(null);
    }
  };

  const truncateText = (text, maxLength = 100) => {
    if (!text) return '';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  const formatTopic = (topic) => {
    if (!topic) return '';
    return topic.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const getDifficultyBadgeClass = (difficulty) => {
    switch (difficulty?.toLowerCase()) {
      case 'easy': return 'badge-success';
      case 'medium': return 'badge-warning';
      case 'hard': return 'badge-danger';
      default: return 'badge-secondary';
    }
  };

  const getTypeBadgeClass = (type) => {
    switch (type) {
      case 'multiple_choice': return 'badge-primary';
      case 'true_false': return 'badge-info';
      case 'fill_in_blank': return 'badge-secondary';
      case 'short_answer': return 'badge-dark';
      default: return 'badge-light';
    }
  };

  const formatQuestionType = (type) => {
    if (!type) return '';
    return type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  // Export functions
  const exportToCSV = async () => {
    try {
      console.log('Starting CSV export...');
      // toast.info('Preparing CSV export...');
      
      // Fetch all questions with current filters using pagination
      let allQuestions = [];
      let currentPage = 1;
      const pageSize = 100; // Maximum allowed by backend
      let hasMorePages = true;
      
      const baseParams = {
        page_size: pageSize,
        ...(filters.search && { search: filters.search }),
        ...(filters.topic && { topic: filters.topic }),
        ...(filters.difficulty && { difficulty: filters.difficulty }),
        ...(filters.questionType && { question_type: filters.questionType }),
        sort_by: filters.sortBy,
        sort_order: filters.sortOrder
      };
      
      while (hasMorePages) {
        const params = { ...baseParams, page: currentPage };
        console.log(`Fetching page ${currentPage} with params:`, params);
        
        const data = await getQuestions(params);
        const questions = data.questions || [];
        
        allQuestions = [...allQuestions, ...questions];
        
        // Check if we have more pages
        hasMorePages = questions.length === pageSize && currentPage < (data.total_pages || 1);
        currentPage++;
        
        // Safety check to avoid infinite loop
        if (currentPage > 1000) {
          console.warn('Reached maximum page limit (1000) during export');
          break;
        }
      }
      
      console.log(`Found ${allQuestions.length} total questions to export`);
      const exportQuestions = allQuestions;
      
      if (exportQuestions.length === 0) {
        toast.warning('No questions to export');
        return;
      }

      // Create CSV content
      const headers = ['ID', 'Question Text', 'Question Type', 'Difficulty', 'Topic', 'Options', 'Correct Answer', 'Explanation', 'Created At'];
      const csvRows = [headers];

      exportQuestions.forEach(q => {
        try {
          const options = q.options ? JSON.stringify(parseOptions(q.options)) : '[]';
          const row = [
            q.id || '',
            `"${(q.question_text || '').replace(/"/g, '""')}"`,
            q.question_type || '',
            q.difficulty || '',
            q.topic || '',
            `"${options.replace(/"/g, '""')}"`,
            `"${(q.correct_answer || '').replace(/"/g, '""')}"`,
            `"${(q.explanation || '').replace(/"/g, '""')}"`,
            q.created_at || ''
          ];
          csvRows.push(row);
        } catch (rowError) {
          console.error('Error processing question for CSV:', q, rowError);
        }
      });

      const csvContent = csvRows.map(row => row.join(',')).join('\n');
      console.log('CSV content created, length:', csvContent.length);
      
      downloadFile(csvContent, 'questions_export.csv', 'text/csv');
      
      toast.success(`Exported ${exportQuestions.length} questions to CSV`);
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export: ' + (error.message || 'Unknown error'));
    }
  };

  const exportToJSON = async () => {
    try {
      toast.info('Preparing JSON export...');
      
      // Fetch all questions with current filters
      const params = {
        page: 1,
        page_size: 10000,
        ...(filters.search && { search: filters.search }),
        ...(filters.topic && { topic: filters.topic }),
        ...(filters.difficulty && { difficulty: filters.difficulty }),
        ...(filters.questionType && { question_type: filters.questionType }),
        sort_by: filters.sortBy,
        sort_order: filters.sortOrder
      };
      
      const data = await getQuestions(params);
      const exportQuestions = data.questions || [];
      
      if (exportQuestions.length === 0) {
        toast.warning('No questions to export');
        return;
      }

      // Parse options for each question
      const formattedQuestions = exportQuestions.map(q => ({
        ...q,
        options: q.options ? parseOptions(q.options) : []
      }));

      const jsonContent = JSON.stringify(formattedQuestions, null, 2);
      downloadFile(jsonContent, 'questions_export.json', 'application/json');
      
      toast.success(`Exported ${exportQuestions.length} questions to JSON`);
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export: ' + error.message);
    }
  };

  const downloadFile = (content, filename, mimeType) => {
    const blob = new Blob([content], { type: mimeType });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="container-fluid">
      {/* Page Header */}
      <div className="d-sm-flex align-items-center justify-content-between mb-4">
        <h1 className="h3 mb-0 text-gray-800">Questions Management</h1>
        <div>
          <Link
            to="/admin"
            className="btn btn-sm btn-secondary mr-2"
          >
            <i className="fas fa-arrow-left mr-1"></i>
            Back to Dashboard
          </Link>
          <button
            type="button"
            className="btn btn-sm btn-success mr-2"
            onClick={exportToCSV}
          >
            <i className="fas fa-file-csv mr-1"></i>
            Export CSV
          </button>
          <Link to="/admin/questions/new" className="d-none d-sm-inline-block btn btn-sm btn-primary shadow-sm">
            <i className="fas fa-plus fa-sm text-white-50"></i> Add New Question
          </Link>
        </div>
      </div>

      {/* Filters Card */}
      <div className="card shadow mb-4">
        <div className="card-header py-3">
          <h6 className="m-0 font-weight-bold text-primary">Filters & Search</h6>
        </div>
        <div className="card-body">
          <div className="row">
            {/* Search */}
            <div className="col-md-4 mb-3">
              <label htmlFor="search" className="form-label small">Search Question Text</label>
              <input
                type="text"
                id="search"
                className="form-control form-control-sm"
                placeholder="Search questions..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
            </div>

            {/* Topic Filter */}
            <div className="col-md-2 mb-3">
              <label htmlFor="topic" className="form-label small">Topic</label>
              <select
                id="topic"
                className="form-control form-control-sm"
                value={filters.topic}
                onChange={(e) => handleFilterChange('topic', e.target.value)}
              >
                <option value="">All Topics</option>
                {topics.map(topic => (
                  <option key={topic} value={topic}>{formatTopic(topic)}</option>
                ))}
              </select>
            </div>

            {/* Difficulty Filter */}
            <div className="col-md-2 mb-3">
              <label htmlFor="difficulty" className="form-label small">Difficulty</label>
              <select
                id="difficulty"
                className="form-control form-control-sm"
                value={filters.difficulty}
                onChange={(e) => handleFilterChange('difficulty', e.target.value)}
              >
                <option value="">All Levels</option>
                <option value="easy">Easy</option>
                <option value="medium">Medium</option>
                <option value="hard">Hard</option>
              </select>
            </div>

            {/* Question Type Filter */}
            <div className="col-md-2 mb-3">
              <label htmlFor="questionType" className="form-label small">Question Type</label>
              <select
                id="questionType"
                className="form-control form-control-sm"
                value={filters.questionType}
                onChange={(e) => handleFilterChange('questionType', e.target.value)}
              >
                <option value="">All Types</option>
                <option value="multiple_choice">Multiple Choice</option>
                <option value="true_false">True/False</option>
                <option value="fill_in_blank">Fill in Blank</option>
                <option value="short_answer">Short Answer</option>
              </select>
            </div>

            {/* Sort By */}
            <div className="col-md-1 mb-3">
              <label htmlFor="sortBy" className="form-label small">Sort By</label>
              <select
                id="sortBy"
                className="form-control form-control-sm"
                value={filters.sortBy}
                onChange={(e) => handleFilterChange('sortBy', e.target.value)}
              >
                <option value="id">ID</option>
                <option value="topic">Topic</option>
                <option value="difficulty">Difficulty</option>
              </select>
            </div>

            {/* Sort Order */}
            <div className="col-md-1 mb-3">
              <label htmlFor="sortOrder" className="form-label small">Order</label>
              <select
                id="sortOrder"
                className="form-control form-control-sm"
                value={filters.sortOrder}
                onChange={(e) => handleFilterChange('sortOrder', e.target.value)}
              >
                <option value="asc">Asc</option>
                <option value="desc">Desc</option>
              </select>
            </div>
          </div>

          <div className="row">
            <div className="col-12">
              <button
                className="btn btn-sm btn-secondary"
                onClick={handleClearFilters}
              >
                <i className="fas fa-times"></i> Clear Filters
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Questions Table Card */}
      <div className="card shadow mb-4">
        <div className="card-header py-3">
          <h6 className="m-0 font-weight-bold text-primary">
            Questions List 
            {!loading && (
              <span className="text-muted ml-2" style={{ fontSize: '0.875rem' }}>
                (Showing {((pagination.page - 1) * pagination.pageSize) + 1}-{Math.min(pagination.page * pagination.pageSize, pagination.total)} of {pagination.total})
              </span>
            )}
          </h6>
        </div>
        <div className="card-body">
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="sr-only">Loading...</span>
              </div>
              <p className="mt-2 text-muted">Loading questions...</p>
            </div>
          ) : questions.length === 0 ? (
            <div className="text-center py-5">
              <i className="fas fa-question-circle fa-3x text-muted mb-3"></i>
              <p className="text-muted">No questions found. Try adjusting your filters or add a new question.</p>
              <Link to="/admin/questions/new" className="btn btn-primary mt-2">
                <i className="fas fa-plus"></i> Add New Question
              </Link>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover">
                <thead>
                  <tr>
                    <th style={{ width: '60px' }}>ID</th>
                    <th>Question Text</th>
                    <th style={{ width: '120px' }}>Type</th>
                    <th style={{ width: '100px' }}>Difficulty</th>
                    <th style={{ width: '180px' }}>Topic</th>
                    <th style={{ width: '150px' }} className="text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {questions.map((question) => (
                    <tr key={question.id}>
                      <td>{question.id}</td>
                      <td>
                        <div 
                          title={question.question_text}
                          style={{ cursor: 'pointer' }}
                          onClick={() => navigate(`/admin/questions/${question.id}`)}
                        >
                          {truncateText(question.question_text, 120)}
                        </div>
                      </td>
                      <td>
                        <span className={`badge ${getTypeBadgeClass(question.question_type)}`}>
                          {formatQuestionType(question.question_type)}
                        </span>
                      </td>
                      <td>
                        <span className={`badge ${getDifficultyBadgeClass(question.difficulty)}`}>
                          {question.difficulty || 'N/A'}
                        </span>
                      </td>
                      <td>
                        <small>{formatTopic(question.topic)}</small>
                      </td>
                      <td className="text-center">
                        <Link
                          to={`/admin/questions/${question.id}`}
                          className="btn btn-info btn-sm btn-circle mr-1"
                          title="View Details"
                        >
                          <i className="fas fa-eye"></i>
                        </Link>
                        <Link
                          to={`/admin/questions/${question.id}/edit`}
                          className="btn btn-warning btn-sm btn-circle mr-1"
                          title="Edit Question"
                        >
                          <i className="fas fa-edit"></i>
                        </Link>
                        <button
                          className="btn btn-danger btn-sm btn-circle"
                          title="Delete Question"
                          onClick={() => handleDelete(question.id, question.question_text)}
                          disabled={deleting === question.id}
                        >
                          {deleting === question.id ? (
                            <i className="fas fa-spinner fa-spin"></i>
                          ) : (
                            <i className="fas fa-trash"></i>
                          )}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {!loading && questions.length > 0 && (
            <div className="d-flex justify-content-between align-items-center mt-3">
              <div className="text-muted small">
                Page {pagination.page} of {pagination.totalPages}
              </div>
              <nav>
                <ul className="pagination mb-0">
                  <li className={`page-item ${pagination.page === 1 ? 'disabled' : ''}`}>
                    <button
                      className="page-link"
                      onClick={() => handlePageChange(pagination.page - 1)}
                      disabled={pagination.page === 1}
                    >
                      <i className="fas fa-chevron-left"></i>
                    </button>
                  </li>
                  
                  {/* First page */}
                  {pagination.page > 3 && (
                    <>
                      <li className="page-item">
                        <button className="page-link" onClick={() => handlePageChange(1)}>1</button>
                      </li>
                      {pagination.page > 4 && (
                        <li className="page-item disabled">
                          <span className="page-link">...</span>
                        </li>
                      )}
                    </>
                  )}
                  
                  {/* Page numbers around current page */}
                  {[...Array(pagination.totalPages)].map((_, index) => {
                    const pageNum = index + 1;
                    if (
                      pageNum === pagination.page ||
                      (pageNum >= pagination.page - 2 && pageNum <= pagination.page + 2)
                    ) {
                      return (
                        <li
                          key={pageNum}
                          className={`page-item ${pageNum === pagination.page ? 'active' : ''}`}
                        >
                          <button
                            className="page-link"
                            onClick={() => handlePageChange(pageNum)}
                          >
                            {pageNum}
                          </button>
                        </li>
                      );
                    }
                    return null;
                  })}
                  
                  {/* Last page */}
                  {pagination.page < pagination.totalPages - 2 && (
                    <>
                      {pagination.page < pagination.totalPages - 3 && (
                        <li className="page-item disabled">
                          <span className="page-link">...</span>
                        </li>
                      )}
                      <li className="page-item">
                        <button
                          className="page-link"
                          onClick={() => handlePageChange(pagination.totalPages)}
                        >
                          {pagination.totalPages}
                        </button>
                      </li>
                    </>
                  )}
                  
                  <li className={`page-item ${pagination.page === pagination.totalPages ? 'disabled' : ''}`}>
                    <button
                      className="page-link"
                      onClick={() => handlePageChange(pagination.page + 1)}
                      disabled={pagination.page === pagination.totalPages}
                    >
                      <i className="fas fa-chevron-right"></i>
                    </button>
                  </li>
                </ul>
              </nav>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuestionsList;
